/**
 * Recruiter bosh sahifasi -- "Bugun" (redesign v2): salomlashish va qo'ng'iroq oynasi, 4 ko'rsatkich (yangi, yuborilgan,
 * ishga olingan, bonus), bugungi vazifalar, e'tibor kerak driverlar, mening pipeline'im, oxirgi o'zgarishlar, reyting.
 * Analitika yo'q: faqat o'z ishi. Sahifadagi yagona sariq tugma -- "Driver qo'shish".
 */
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { formatMoney } from "@/lib/utils";
import type { Me } from "@/features/auth/types";
import type { Attention, DashboardData } from "@/features/dashboards/types";
import { periodMonth, prevPeriodMonth } from "@/features/dashboards/api";
import { PeriodSelect } from "@/features/dashboards/blocks";
import { Button } from "@/components/ui/button";
import { KpiCard } from "@/components/ui/card";
import { AnimatedNumber, AttentionCard, Grid, HomeHero, LeaderCard, RecentCard, StageStrip, TasksCard, sampleLine, type AttentionRow } from "./parts";
import { deltaChip, durationLabel, useHomeClock } from "./clock";

export interface HomeProps {
  me: Me;
  data: DashboardData;
  period: string;
  setPeriod: (value: string) => void;
}

type T = (key: string, opts?: Record<string, unknown>) => string;

/** Driver signallari (recruiter va menejer uchun bir xil): faqat nol bo'lmaganlari */
export function driverAttentionRows(a: Attention, t: T): AttentionRow[] {
  const rows: AttentionRow[] = [];
  if (a.no_contact.count) rows.push({ key: "no_contact", count: a.no_contact.count, text: t("attention.noContact", { days: a.stale.days ?? 7 }), sub: sampleLine(a.no_contact.sample, a.no_contact.count, t), tone: "warn", to: "/drivers?problem=no_contact" });
  if (a.waiting_docs.count) rows.push({ key: "docs", count: a.waiting_docs.count, text: t("attention.waitingDocs"), sub: sampleLine(a.waiting_docs.sample, a.waiting_docs.count, t), tone: "ink", to: "/drivers?problem=docs_missing" });
  if (a.docs_expired?.count) rows.push({ key: "docs_expired", count: a.docs_expired.count, text: t("attention.docsExpired"), sub: sampleLine(a.docs_expired.sample, a.docs_expired.count, t), tone: "bad", to: "/drivers?problem=docs_expired" });
  if (a.cdl_expiring.count) {
    const first = a.cdl_expiring.sample?.[0];
    rows.push({
      key: "cdl",
      count: a.cdl_expiring.count,
      text: a.cdl_expiring.count === 1 && first?.days !== undefined ? t("attention.cdlOne", { days: first.days }) : t("attention.cdl"),
      sub: sampleLine(a.cdl_expiring.sample, a.cdl_expiring.count, t),
      tone: "bad",
      to: "/drivers?problem=docs_expiring",
    });
  }
  if (a.carrier_pending.count) rows.push({ key: "carrier", count: a.carrier_pending.count, text: t("attention.carrier"), sub: sampleLine(a.carrier_pending.sample, a.carrier_pending.count, t), tone: "ink", to: "/drivers?status=submitted" });
  return rows;
}

export function RecruiterHome({ me, data, period, setPeriod }: HomeProps) {
  const { t } = useTranslation("home");
  const { t: ti } = useTranslation("metrics");
  const { t: tc } = useTranslation();
  const clock = useHomeClock(me.timezone);
  const k = data.kpis;
  const month = periodMonth(period);
  const prev = prevPeriodMonth(period);
  const tz = me.timezone || "Asia/Tashkent";
  const overdue = data.tasks?.counts.overdue ?? 0;
  const newDelta = deltaChip(k.new_drivers - k.new_drivers_prev);
  const callLine = clock.call.open
    ? t("callOpen", { time: clock.dallas, left: durationLabel(clock.call.minutes, tc) })
    : t("callClosed", { time: clock.dallas, at: clock.callStart, wait: durationLabel(clock.call.minutes, tc) });
  const active = Object.values(data.stages).reduce((n, v) => n + (v ?? 0), 0);
  const attention = driverAttentionRows(data.attention, t);
  const myRank = data.leaderboard.findIndex((r) => r.user_id === me.id);

  return (
    <div className="flex flex-col gap-6">
      <HomeHero
        eyebrow={t("eyebrowShort", { date: clock.date, tashkent: clock.tashkent })}
        title={t(`greeting.${clock.greeting}`, { name: me.first_name })}
        line={`${overdue ? t("overdue", { count: overdue }) : t("nothingOverdue")} · ${callLine}`}
        actions={
          <>
            <PeriodSelect value={period} onChange={setPeriod} />
            <Link to="/drivers/new">
              <Button variant="primary" size="lg" className="shadow-accent">
                <Plus size={16} strokeWidth={2.25} aria-hidden /> {t("addDriver")}
              </Button>
            </Link>
          </>
        }
      />

      <Grid className="grid-cols-[repeat(auto-fit,minmax(min(230px,100%),1fr))]">
        <KpiCard to={`/drivers?added_month=${period}`} info={ti("newDrivers")} label={`${t("kpi.newDrivers")} · ${month}`} value={<AnimatedNumber value={k.new_drivers} />} chip={newDelta.label} chipTone={newDelta.tone} sub={t("kpi.vs", { month: prev })} />
        <KpiCard to={`/drivers?submitted_month=${period}`} info={ti("submitted")} label={t("kpi.submitted")} value={<AnimatedNumber value={k.submitted} />} chip={t("kpi.waiting", { count: k.waiting_reply })} sub={t("kpi.forReply")} />
        <KpiCard
          to={`/drivers?hired_month=${period}`}
          info={ti("hires")} label={t("kpi.hired")}
          value={<AnimatedNumber value={k.hires} />}
          chip={k.hires_pending ? t("kpi.pending", { count: k.hires_pending }) : undefined}
          chipTone="warning"
          sub={k.hires_pending ? undefined : t("kpi.approved")}
        />
        <KpiCard
          info={ti("myBonus")} label={`${t("kpi.myBonus")} · ${month}`}
          value={data.bonus ? formatMoney(data.bonus.amount, data.bonus.currency) : "—"}
          chip={data.bonus && data.bonus.status !== "estimate" ? tc(`status.${data.bonus.status}`, { defaultValue: data.bonus.status }) : t("kpi.estimate")}
          chipTone="accent"
          sub={t("kpi.finalAfter")}
          accent="budget"
        />
      </Grid>

      <Grid className="grid-cols-1 xl:grid-cols-3">
        {data.tasks && <TasksCard tasks={data.tasks} tz={tz} className="xl:col-span-2" />}
        <AttentionCard rows={attention} />
      </Grid>

      <StageStrip stages={data.stages} title={t("pipeline.title")} sub={t("pipeline.active", { count: active })} to="/pipeline" />

      <Grid className="grid-cols-1 xl:grid-cols-3">
        <RecentCard drivers={data.recently_updated} className="xl:col-span-2" />
        <LeaderCard
          rows={data.leaderboard}
          meId={me.id}
          title={data.leaderboard_label ?? t("manager.team")}
          sub={t("leader.sub", { month })}
          footer={
            <>
              {myRank >= 0 && <span>{t("leader.rank", { pos: myRank + 1, of: data.leaderboard.length })}</span>}
              <Link to={`/people/${me.id}`} className="ml-auto font-medium text-primary hover:underline">
                {t("leader.myPage")}
              </Link>
            </>
          }
        />
      </Grid>
    </div>
  );
}
